<script>
	import { user, token } from "../stores.js";
	
	import SecretHider from "../components/SecretHider.svelte";
	import Spinner from "../components/Spinner.svelte";
	import SpinnerLock from "../components/SpinnerLock.svelte";
	import { minDelay } from "../utils.js";
	import { apiPost } from '../api.js';
	import { defaultExpiry, maxExpiry, expiryToMinutes, expiryShortcuts, formatLocalDateTime } from '../expiry.js';

	const base_url = window.location.origin;
	const url = "SECURESHARE_API_URL";
	const api_key = "SECURESHARE_API_KEY";
	const MAX_BURN_AFTER_READING = 10;
	const MAX_SECRET_LENGTH = 10000;
    const MAX_SECRET_FILE_SIZE_IN_BYTES = 524288000; //500MB
    let fileinput = $state();

	let urlCopied = $state(false);
	async function copyUrl(url) {
		await navigator.clipboard.writeText(url);
		urlCopied = true;
		setTimeout(() => urlCopied = false, 2000);
	}

	let secretId = $state("");
	let id_generation_mode = $state("WORDS");
	let secretText = $state("");
	let password = $state("");
    let confirmPassword = $state("");
    let email = $state("");
    let confirmEmail = $state("");
	let burnAfterReading = $state(10);
	let expiryDateTime = $state(defaultExpiry());
	const latestExpiry = maxExpiry();
	const shortcuts = expiryShortcuts();
	let savePromise = $state({});
	// A pending promise keeps `{#await signedLinkPromise}` on its spinner
	// branch until the reactive below replaces it with the real fetch.
	// Previously this was `{}`, which `{#await}` treats as already-resolved
	// and briefly flashed the file picker before onMount swapped in the
	// real promise.
	let signedLinkPromise = $state(new Promise(() => {}));
	let signedLink = {};
	let saveRequested = $state(false);
	let validation_errors = $state([]);
    let file_id = "";
	let file_name = "";
	let note = $state("");
	let memo = $state("");

	user.useLocalStorage();

    async function saveFileHttp(file) {
		let formData = new FormData();

		formData.append("key", signedLink.fields.key);
		formData.append("AWSAccessKeyId", signedLink.fields.AWSAccessKeyId);
		formData.append("policy", signedLink.fields.policy);
		formData.append("signature", signedLink.fields.signature);
		formData.append("x-amz-security-token", signedLink.fields["x-amz-security-token"]);
		formData.append("file", file);

		const res = await fetch(`${signedLink.url}`, {
			method: "POST",
			body: formData
		});

		if (res.ok) {
			file_id = signedLink.fields.key;
			return true;
		} else {
			throw new Error(json);
		}
    }

    let fileUploading = $state(false);
    let fileReady = $state(false);
    let fileError = $state(false);

    const onFileSelected = async (e) => {
		if (!e.target.files[0]) return;
		if (e.target.files[0].size > MAX_SECRET_FILE_SIZE_IN_BYTES) {
			alert(`File is too large.  Maximum file size: ${MAX_SECRET_FILE_SIZE_IN_BYTES/1024/1024}MB`);
			e.target.value = '';
			return;
		}

        let file = e.target.files[0];
        file_name = file.name;
        fileUploading = true;
        fileReady = false;
        fileError = false;

        try {
            await saveFileHttp(file);
            fileReady = true;
        } catch {
            fileError = true;
        }
        fileUploading = false;
    }
	
    async function getS3PresignedSaveLink() {
		const res = await apiPost(`${url}/s3_presigned_save_generator`, {}, { token: $token, apiKey: api_key });
		signedLink = await res.json();
		
		if (res.ok) {
			return true;
		} else {
			throw new Error(json);
		}
	}

    async function saveSecret() {
		const secretRequest = {
			id: secretId,
			id_generation_mode: id_generation_mode,
			text: secretText,
			password: password,
			to_address: email,
			burn_after_reading: burnAfterReading,
			expiry_in_minutes: expiryToMinutes(expiryDateTime),
            file_id: file_id,
			file_name: file_name,
			creator_id: $user.id,
			note: note || undefined,
			memo: memo
		};
		
		const res = await apiPost(`${url}/save_sse`, secretRequest, { token: $token, apiKey: api_key });
		const json = await res.json();
		if (res.ok) {
			return { "id" : json.id, "password" : json.password };
		} else {
			throw new Error(json);
		}
	}

	function sendSecret() {
		let valid = validate();
		if (valid) {
			saveRequested = true;
			savePromise = minDelay(saveSecret());
		}
	}

	let passwordSupplied = $derived(password !== "" && password.trim().length > 0 && password === confirmPassword);
    let emailSupplied = $derived(email !== "" && email.trim().length > 0 && email === confirmEmail);
    let inputValid = $derived(memo.trim() !== "" && secretText !== "" && (password === "" || passwordSupplied) && (email === "" || emailSupplied));
	
	function validate() {
		validation_errors = [];
		if (memo.trim() === "") {
			validation_errors = [...validation_errors, "Memo is required."];
		}

		if (secretText.trim() === "") {
			validation_errors = [...validation_errors, "Message text is required."];
		}

		if (password !== "" && password.trim().length > 0 && password !== confirmPassword) {
			validation_errors = [...validation_errors, "Confirmation password must match password."];
		}

		if (email !== "" && email.trim().length > 0 && email !== confirmEmail) {
			validation_errors = [...validation_errors, "Confirmation email must match email."];
		}

		return validation_errors.length === 0;
	}

	// Wait for the token to land before firing the presigned-URL fetch.
	// Login.svelte populates $token asynchronously in its own onMount,
	// and ShareFile's own onMount races that — without this gate the
	// initial request goes out with `Bearer ` (empty) and the API
	// correctly returns 401, surfacing in the template as "Cannot
	// upload files at this time." Same pattern as MySecrets /
	// MyGuestLinks / the home-page table fetches.
	$effect(() => {
		if ($token) signedLinkPromise = getS3PresignedSaveLink();
	});
</script>

<main class="container">
	<h1>Send Message</h1>
	<p class="page-intro">Your message will be encrypted and accessible only with a password. If you provide an email, the link and password will be delivered automatically. Otherwise, you'll receive them here to share manually.</p>

	<div id="errors" class:hidden={!validation_errors}>
		<ul>
		{#each validation_errors as err, i}
			<li>{err}</li>
		{/each}
		</ul>
	</div>
	<div class:hidden={!saveRequested}>
		{#await savePromise}
			<SpinnerLock></SpinnerLock>
		{:then secret}
            <p>All set. If you entered an email address, the link will be automatically delivered to the client.
				Otherwise, you can share the URL below manually. 
				It is advisable to share the password via a secondary channel (not in the same email).
			 </p>
			 <table class="table">
				 <tbody>
					 <tr>
						 <td><b>URL</b></td>
						 <td>
							{base_url}/read/{secret.id}
							<button class="copy-btn" onclick={() => copyUrl(`${base_url}/read/${secret.id}`)} title="Copy URL to clipboard">
								{#if urlCopied}
									<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/></svg>
								{:else}
									<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/><path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/></svg>
								{/if}
							</button>
						 </td>
					 </tr>
					 <tr>
						 <td><b>Password</b></td>
						 <td><SecretHider secret={secret.password}></SecretHider></td>
					 </tr>
				 </tbody>
			 </table>
 
			 <p><a href="/">Send Another</a></p>
		{:catch error}
			<p class="alert alert-danger">Failed to save message.</p>
		{/await}
	</div>
	<div class:hidden={saveRequested}>
		<div class="form-item">
			<label for="memo" class="form-label">Memo <span class="required">*</span></label>
			<span class="hint">A short message the recipient will see before they enter the password — give them context for what they're about to open. <strong>Not encrypted</strong> — do not include sensitive information here.</span>
			<input name="memo" type="text" bind:value={memo} maxlength=500 class="form-control" autocomplete="off" required>
		</div>

		<div class="form-item">
			<label for="note" class="form-label">Internal Note</label>
			<span class="hint"><i>Optional</i> - For your reference only. The recipient will never see this.</span>
			<input name="note" type="text" bind:value={note} maxlength=500 class="form-control" autocomplete="off">
		</div>

		<div>
            <label for="email" class="form-label">Email</label>
            <span class="hint"><i>Optional</i> - If provided, will send an email automatically with the link to access the message. Using the verbose format (Name &lt;email&gt;) is recommended to avoid triggering spam filters.</span>
            <input name="email" type="email" placeholder="John Doe <jdoe@careevolution.com>" bind:value={email} maxlength=200 class="form-control" autocomplete="off">
        </div>
		<div class:hidden={email === ""}>
            <div class="form-item">
				<label for="confirm_email" class="form-label">Confirm Email</label>
                <input name="confirm_email" type="email" bind:value={confirmEmail} maxlength=200 class="form-control" autocomplete="off">
			</div>
        </div>

		<div class="form-item">
            <label for="secret_text" class="form-label">Message</label>
            <textarea name="secret_text" style="width: 100%; height: 400px" type="text" bind:value={secretText} maxlength={MAX_SECRET_LENGTH} class="form-control" autocomplete="off"></textarea>
        </div>

		<div class="form-item">
			{#await signedLinkPromise}
				<Spinner></Spinner>
			{:then res}
				<label for="secret_file" class="form-label">Attachment <span class="hint">(optional)</span></label>
				<input type="file" onchange={(e)=>onFileSelected(e)} bind:this={fileinput} class="form-control" disabled={fileUploading}>
				{#if fileUploading}
					<span class="file-status uploading">Uploading...</span>
				{:else if fileReady}
					<span class="file-status ready">Ready</span>
				{:else if fileError}
					<span class="file-status error">Upload failed. Try again.</span>
				{/if}
			{:catch error}
				<p style="color: red">Cannot upload files at this time.</p>
			{/await}
		</div>

		<div class="row limits">
			<div class="col col-6">
				<label for="expiry" class="form-label">Expiry</label>
				<span class="hint">The link and message will be permanently deleted after this time.</span>
				<input type="datetime-local" bind:value={expiryDateTime} max={latestExpiry} class="form-control">
				<div class="expiry-shortcuts">
					{#each shortcuts as s}
						<button type="button" class="btn btn-sm btn-outline-secondary" onclick={() => expiryDateTime = formatLocalDateTime(Date.now() + s.ms)}>{s.label}</button>
					{/each}
				</div>
			</div>
			<div class="col col-6">
				<label for="burn_after_reading" class="form-label">Burn After Reading</label>
				<span class="hint">The message will be permanently deleted after this many successful retrievals.</span>
				<div>
					<input type="range" bind:value={burnAfterReading} min=1 max={MAX_BURN_AFTER_READING}>
					<input type="number" bind:value={burnAfterReading} min=1 max={MAX_BURN_AFTER_READING}>
				</div>
			</div>
		</div>

		<details class="section">
			<summary>Advanced</summary>
			<div class="section-body">
				<p class="section-intro">These options are most useful when sharing a link manually, especially verbally — for example, choosing a memorable ID or setting your own password.</p>
				<div class="form-item">
				<label for="id" class="form-label">ID</label>
				<span class="hint"><i>Optional</i> - will be auto-generated if not provided. Appears in the message URL.</span>
				<input name="id" type="text" bind:value={secretId} maxlength=100 class="form-control" autocomplete="off">
				</div>

				<div class="form-item">
					<label for="id_generation_mode" class="form-label">ID Format</label>
					<span class="hint">When using an auto-generated ID, you may choose whether it will use numbers or words.</span>
					<div class:hidden={secretId !== ""}>
						<label>
							<input name="id_generation_mode" value="AUTO" bind:group={id_generation_mode} type="radio">
							Number (e.g., <i>82c2120a4c794ac4bb94142c34ebc99d03855b4f52de4411af663d5f382db9b6</i>)
						</label>
						<br>
						<label>
							<input name="id_generation_mode" value="WORDS" bind:group={id_generation_mode} type="radio">
							Words (e.g., <i>CalmPurchaseWaterActRailSea</i>)
						</label>
					</div>
				</div>

				<div class="form-item">
					<label for="password" class="form-label">Password</label>
					<span class="hint"><i>Optional</i> - will be auto-generated if not provided.</span>
					<input name="password" type="password" bind:value={password} maxlength=100 class="form-control" autocomplete="off">
				</div>

				<div class:hidden={password === ""}>
					<div class="form-item">
						<label for="confirm_password" class="form-label">Confirm Password</label>
						<input name="confirm_password" type="password" bind:value={confirmPassword} maxlength=100 class="form-control" autocomplete="off">
					</div>
				</div>
			</div>
		</details>

        <div>
	        <button onclick={sendSecret} class="btn btn-primary">Send</button>
	        <a href="/" class="btn btn-outline-secondary" style="margin-left: .5rem">Cancel</a>
        </div>

        {#if validation_errors.length > 0}
    	    <div id="errors" class="alert alert-warning">
                <h2>Errors Found</h2>
        		<ul>
        		{#each validation_errors as err, i}
        			<li>{err}</li>
        		{/each}
        		</ul>
    	    </div>
        {/if}
</main>

<style>
	.page-intro {
		color: #6c757d;
		margin-bottom: 1.5rem;
	}

	#errors h2 {
        font-size: 20px;
    }
      
    table td {
        padding: 10px;
    }

    #errors {
          margin-top: 20px;
    }

	.copy-btn {
		background: none;
		border: none;
		padding: 0 .25rem;
		cursor: pointer;
		color: #6c757d;
		vertical-align: middle;
	}

	.required {
		color: #dc3545;
		margin-left: .25rem;
	}

	.copy-btn:hover {
		color: #0d6efd;
	}

	.limits {
		margin: 1rem 0;
	}

	.expiry-shortcuts {
		margin-top: .5rem;
		display: flex;
		gap: .25rem;
	}

	.expiry-shortcuts :global(.btn) {
		font-size: 75%;
		padding: .15rem .35rem;
	}

	.section {
		margin: 1rem 0;
		border: 1px solid #dee2e6;
		border-radius: .25rem;
	}

	.section summary {
		padding: .75rem 1rem;
		font-weight: 600;
		cursor: pointer;
		background: #f8f9fa;
		border-radius: .25rem;
	}

	.section[open] summary {
		border-bottom: 1px solid #dee2e6;
		border-radius: .25rem .25rem 0 0;
	}

	.section-body {
		padding: .75rem 1rem;
	}

	.section-intro {
		color: #6c757d;
		font-size: 14px;
		margin-bottom: 1rem;
	}

	.file-status {
		display: inline-block;
		margin-top: .5rem;
		font-size: 14px;
		font-weight: 600;
	}

	input[type=file] {
		max-width: 400px;
		font-size: 85%;
	}

	input[type=datetime-local] {
		max-width: 250px;
	}

	.file-status.uploading { color: #0d6efd; }
	.file-status.ready { color: #198754; }
	.file-status.error { color: #dc3545; }

	input[type=text], input[type=range] {
		width: 500px
	}
	
    input[type=range] {
		position: relative;
		top: 7px;
	}

	input[type=number] {
		width: 70px;
		padding-left: 0;
		padding-left: .75rem;
		border-radius: .25rem;
	}	

</style>